import { App } from "../../../../../../../shared/frontend/ui";
import type { UnitsSettings } from "../../../app/units/units";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { Alert, Button } from "../../../../../../../shared/frontend/ui";
import React from "react";
import type { useAuth } from "../../../app/auth/useAuth";
import { spacing } from "../../../app/design-system";
import type { useI18n } from "../../../app/i18n/useI18n";
import { chargebackReasonLabel } from "../../../app/utils/chargebackReason";
import { SepaBankDetailsFields } from "../../../components/SepaBankDetailsFields";
import { SepaBlockManageDrawer } from "../../../components/SepaBlockManageDrawer";
import { PersonPaymentProfileDrawer } from "./PersonPaymentProfileDrawer";
import { authQueryScope } from "../../../app/query/authQueryScope";
import type { PersonDto } from "../types";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

type PersonWithSepaBlockDetails = PersonDto & {
  sepaDirectDebitBlockedUntil?: string | null;
  sepaDirectDebitBlockReasonName?: string | null;
};

type MessageApi = {
  success: (content: string) => void;
  error: (content: string) => void;
};

type Props = {
  person: PersonWithSepaBlockDetails | null;
  personId: string | undefined;
  canWriteFinance: boolean;
  auth: ReturnType<typeof useAuth>;
  message: MessageApi;
  form: FormInstance;
  modal: { confirm: ReturnType<typeof App.useApp>["modal"]["confirm"] };
  text: ReturnType<typeof useI18n>["text"];
  units: UnitsSettings;
  dateInputFormat: string | string[];
  formatDateOnly: (value: string, units: UnitsSettings) => string;
  load: () => Promise<void>;
  setPersonBankName: (value: string | null) => void;
};

export function PersonBankDetailsSection({
  person,
  personId,
  canWriteFinance,
  auth,
  form,
  modal,
  text,
  units,
  dateInputFormat,
  formatDateOnly,
  load,
  setPersonBankName
}: Props) {
  const [manageDrawerOpen, setManageDrawerOpen] = React.useState(false);
  return (
    <>
      {personId && auth.hasAnyPermission("payments.read", "payments.write") && <PersonPaymentProfileDrawer key={JSON.stringify([...authQueryScope(auth.api, auth.info), personId])} personId={personId} canWrite={canWriteFinance} />}
      {person?.isSepaDirectDebitBlocked && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: spacing.xl }}
          message={text("SEPA direct debit blocked")}
          description={(() => {
            const base = text("This person is blocked for SEPA direct debits (e.g. after a chargeback). You can unblock after clarification.");
            const until = person.sepaDirectDebitBlockedUntil;
            const reason = chargebackReasonLabel(person.sepaDirectDebitBlockReasonName);
            const note = person.sepaBlockNote;
            const parts = [base];
            if (until) parts.push(text("Block until") + ": " + formatDateOnly(String(until).slice(0, ISO_DATE_LENGTH), units));
            if (reason) parts.push(text("Reason") + ": " + reason);
            if (note) parts.push(text("SEPA block note") + ": " + note);
            return parts.join(" ");
          })()}
          action={canWriteFinance && personId ? (
            <Button size="small" type="primary" onClick={() => setManageDrawerOpen(true)}>
              {text("Manage SEPA block")}
            </Button>
          ) : undefined}
        />
      )}

      {personId ? (
        <SepaBlockManageDrawer
          open={manageDrawerOpen}
          onClose={() => setManageDrawerOpen(false)}
          personId={personId}
          onChanged={() => load().catch(() => {})}
        />
      ) : null}

      <div style={{ marginTop: spacing.xl }}>
        <SepaBankDetailsFields
          api={auth.api}
          autoGenerateMandateOnIban={false}
          dateInputFormat={dateInputFormat}
          firstName={person?.firstName}
          form={form}
          homeStudioId={person?.homeStudioId}
          lastName={person?.lastName}
          modal={modal}
          personId={personId}
          setBankName={setPersonBankName}
          showTitle={false}
          text={text}
        />
      </div>
    </>
  );
}
